import { useState, useEffect, useRef, useCallback } from 'react'
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Input,
  FormControl,
  FormLabel,
  FormHelperText,
  Select,
  Textarea,
  Switch,
  Radio,
  RadioGroup,
  Stack,
  useToast,
  Badge,
  Spinner,
  AlertDialog,
  AlertDialogOverlay,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogBody,
  AlertDialogFooter,
  Alert,
  AlertIcon,
  useColorModeValue,
  useBreakpointValue,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  DrawerBody,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Avatar,
  Tooltip,
} from '@chakra-ui/react'
import {
  Plus,
  PencilSimple,
  Trash,
  List,
  UsersThree,
  Play,
  PaperPlaneTilt,
  Wrench,
  Eraser,
} from '@phosphor-icons/react'
import api from '../services/api'
import { Sidebar } from '../layouts/DefaultLayout'
import AiChatsInbox, { type AiChatSummary } from './AiChatsInbox'

interface AiAgent {
  id: number
  name: string
  categoryId: number
  botUserId: number | null
  provider: 'anthropic' | 'openai'
  model: string
  apiToken: string | null
  hasToken: boolean
  responsePrompt: string | null
  isActive: number
  activationMode: 'selected' | 'all'
  categoryName: string | null
  categoryColor: string | null
  userCount: number
}

interface Category {
  id: number
  name: string
}

interface LinkedUser {
  id: number
  name: string
  email: string
}

interface PlaygroundMessage {
  role: 'user' | 'assistant'
  content: string
  toolCalls?: { name: string; input: Record<string, any> }[]
  usage?: { inputTokens: number; outputTokens: number }
}


const PROVIDER_LABELS: Record<string, string> = {
  anthropic: 'Claude (Anthropic)',
  openai: 'OpenAI',
}

const emptyForm = {
  name: '',
  categoryId: '',
  provider: 'anthropic' as 'anthropic' | 'openai',
  model: '',
  apiToken: '',
  responsePrompt: '',
  isActive: false,
  activationMode: 'selected' as 'selected' | 'all',
}

export default function AiAgents() {
  const [agents, setAgents] = useState<AiAgent[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [models, setModels] = useState<Record<string, string[]>>({ anthropic: [], openai: [] })
  const [isLoading, setIsLoading] = useState(true)
  const [selectedAgent, setSelectedAgent] = useState<AiAgent | null>(null)
  const [formData, setFormData] = useState({ ...emptyForm })
  const [isSaving, setIsSaving] = useState(false)

  // Prompt base
  const [basePrompt, setBasePrompt] = useState('')
  const [aiEnabled, setAiEnabled] = useState(true)
  const [isSavingBasePrompt, setIsSavingBasePrompt] = useState(false)

  // Usuários vinculados
  const [linkedUsers, setLinkedUsers] = useState<LinkedUser[]>([])
  const [userSearch, setUserSearch] = useState('')
  const [userResults, setUserResults] = useState<LinkedUser[]>([])
  const [isSearchingUsers, setIsSearchingUsers] = useState(false)
  const [isSavingUsers, setIsSavingUsers] = useState(false)

  // Playground
  const [playgroundMessages, setPlaygroundMessages] = useState<PlaygroundMessage[]>([])
  const [playgroundInput, setPlaygroundInput] = useState('')
  const [isPlaygroundLoading, setIsPlaygroundLoading] = useState(false)
  const playgroundEndRef = useRef<HTMLDivElement>(null)

  // Atendimentos da IA: a caixa de entrada (AiChatsInbox) publica o sumário
  // para o contador da aba
  const [chatSummary, setChatSummary] = useState<AiChatSummary | null>(null)

  const toast = useToast()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const surfaceBg = useColorModeValue('white', 'gray.800')
  const borderColor = useColorModeValue('gray.200', 'gray.700')
  const textPrimary = useColorModeValue('gray.800', 'white')
  const textMuted = useColorModeValue('gray.500', 'gray.400')
  const menuIconColor = useColorModeValue('gray.700', 'white')
  const bubbleUserBg = useColorModeValue('yellow.100', 'yellow.700')
  const bubbleAgentBg = useColorModeValue('gray.100', 'gray.700')
  const toolCardBg = useColorModeValue('orange.50', 'orange.900')

  const { isOpen: isFormOpen, onOpen: onFormOpen, onClose: onFormClose } = useDisclosure()
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure()
  const { isOpen: isUsersOpen, onOpen: onUsersOpen, onClose: onUsersClose } = useDisclosure()
  const { isOpen: isPlaygroundOpen, onOpen: onPlaygroundOpen, onClose: onPlaygroundClose } = useDisclosure()
  const { isOpen: isMenuOpen, onOpen: onMenuOpen, onClose: onMenuClose } = useDisclosure()
  const cancelRef = useRef<HTMLButtonElement>(null)

  const fetchAgents = async () => {
    setIsLoading(true)
    try {
      const res = await api.get('/admin/ai-agents')
      setAgents(res.data.data || [])
    } catch (error) {
      console.error('Error fetching AI agents:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const fetchStaticData = async () => {
    try {
      const [categoriesRes, modelsRes, basePromptRes] = await Promise.all([
        api.get('/admin/categories'),
        api.get('/admin/ai-agents/models'),
        api.get('/admin/ai-agents/settings/base-prompt'),
      ])
      setCategories(categoriesRes.data || [])
      setModels(modelsRes.data.data || { anthropic: [], openai: [] })
      setBasePrompt(basePromptRes.data.basePrompt || '')
      setAiEnabled(basePromptRes.data.aiEnabled !== false)
    } catch (error) {
      console.error('Error fetching AI agents static data:', error)
    }
  }

  useEffect(() => {
    fetchAgents()
    fetchStaticData()
  }, [])

  // ============ CRUD ============

  const handleCreate = () => {
    setSelectedAgent(null)
    setFormData({ ...emptyForm, model: models.anthropic?.[0] || '' })
    onFormOpen()
  }

  const handleEdit = (agent: AiAgent) => {
    setSelectedAgent(agent)
    setFormData({
      name: agent.name,
      categoryId: String(agent.categoryId),
      provider: agent.provider,
      model: agent.model,
      apiToken: '',
      responsePrompt: agent.responsePrompt || '',
      isActive: !!agent.isActive,
      activationMode: agent.activationMode,
    })
    onFormOpen()
  }

  const handleSubmit = async () => {
    if (!formData.name.trim()) {
      toast({ title: 'Nome é obrigatório', status: 'warning', duration: 3000 })
      return
    }
    if (!formData.categoryId) {
      toast({ title: 'Selecione a categoria', status: 'warning', duration: 3000 })
      return
    }
    if (!formData.model) {
      toast({ title: 'Selecione o modelo', status: 'warning', duration: 3000 })
      return
    }

    // Ligar para TODOS os clientes é uma ação sensível em produção
    const turningOnForAll =
      formData.activationMode === 'all' &&
      formData.isActive &&
      (!selectedAgent || selectedAgent.activationMode !== 'all' || !selectedAgent.isActive)
    if (turningOnForAll) {
      const confirmed = window.confirm(
        `Atenção: o agente "${formData.name}" ficará ATIVO para TODOS os clientes da categoria. Confirmar?`
      )
      if (!confirmed) return
    }

    setIsSaving(true)
    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        categoryId: Number(formData.categoryId),
        provider: formData.provider,
        model: formData.model,
        responsePrompt: formData.responsePrompt,
        isActive: formData.isActive,
        activationMode: formData.activationMode,
      }
      if (formData.apiToken.trim()) {
        payload.apiToken = formData.apiToken.trim()
      }

      if (selectedAgent) {
        await api.put(`/admin/ai-agents/${selectedAgent.id}`, payload)
        toast({ title: 'Agente atualizado', status: 'success', duration: 3000 })
      } else {
        await api.post('/admin/ai-agents', payload)
        toast({ title: 'Agente criado', status: 'success', duration: 3000 })
      }
      onFormClose()
      fetchAgents()
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleToggle = async (agent: AiAgent) => {
    if (!agent.isActive && agent.activationMode === 'all') {
      const confirmed = window.confirm(
        `Atenção: o agente "${agent.name}" ficará ATIVO para TODOS os clientes da categoria. Confirmar?`
      )
      if (!confirmed) return
    }
    try {
      await api.post(`/admin/ai-agents/${agent.id}/toggle`)
      setAgents((prev) =>
        prev.map((a) => (a.id === agent.id ? { ...a, isActive: a.isActive ? 0 : 1 } : a))
      )
    } catch (error: any) {
      toast({
        title: 'Erro ao alterar ativação',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  const handleDelete = (agent: AiAgent) => {
    setSelectedAgent(agent)
    onDeleteOpen()
  }

  const handleConfirmDelete = async () => {
    if (!selectedAgent) return
    try {
      await api.delete(`/admin/ai-agents/${selectedAgent.id}`)
      toast({ title: 'Agente excluído', status: 'success', duration: 3000 })
      onDeleteClose()
      fetchAgents()
    } catch (error: any) {
      toast({
        title: 'Erro ao excluir',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    }
  }

  // ============ Prompt base ============

  const handleSaveBasePrompt = async () => {
    if (!basePrompt.trim()) {
      toast({ title: 'O prompt base não pode ser vazio', status: 'warning', duration: 3000 })
      return
    }
    setIsSavingBasePrompt(true)
    try {
      await api.put('/admin/ai-agents/settings/base-prompt', { basePrompt, aiEnabled })
      toast({ title: 'Prompt base salvo', status: 'success', duration: 3000 })
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSavingBasePrompt(false)
    }
  }

  // ============ Usuários vinculados ============

  const handleOpenUsers = async (agent: AiAgent) => {
    setSelectedAgent(agent)
    setUserSearch('')
    setUserResults([])
    setLinkedUsers([])
    onUsersOpen()
    try {
      const res = await api.get(`/admin/ai-agents/${agent.id}`)
      setLinkedUsers(res.data.users || [])
    } catch (error) {
      console.error('Error fetching agent users:', error)
    }
  }

  const searchUsers = useCallback(async (query: string) => {
    if (query.trim().length < 2) {
      setUserResults([])
      return
    }
    setIsSearchingUsers(true)
    try {
      const res = await api.get('/admin/ai-agents/users-search', { params: { q: query.trim() } })
      setUserResults(res.data.data || [])
    } catch (error) {
      console.error('Error searching users:', error)
    } finally {
      setIsSearchingUsers(false)
    }
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => searchUsers(userSearch), 400)
    return () => clearTimeout(timer)
  }, [userSearch, searchUsers])

  const handleAddUser = (user: LinkedUser) => {
    setLinkedUsers((prev) => (prev.some((u) => u.id === user.id) ? prev : [...prev, user]))
  }

  const handleRemoveUser = (userId: number) => {
    setLinkedUsers((prev) => prev.filter((u) => u.id !== userId))
  }

  const handleSaveUsers = async () => {
    if (!selectedAgent) return
    setIsSavingUsers(true)
    try {
      await api.put(`/admin/ai-agents/${selectedAgent.id}/users`, {
        userIds: linkedUsers.map((u) => u.id),
      })
      toast({ title: 'Clientes vinculados atualizados', status: 'success', duration: 3000 })
      onUsersClose()
      fetchAgents()
    } catch (error: any) {
      toast({
        title: 'Erro ao salvar',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 5000,
      })
    } finally {
      setIsSavingUsers(false)
    }
  }

  // ============ Playground ============

  const handleOpenPlayground = (agent: AiAgent) => {
    setSelectedAgent(agent)
    setPlaygroundMessages([])
    setPlaygroundInput('')
    onPlaygroundOpen()
  }

  useEffect(() => {
    playgroundEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [playgroundMessages, isPlaygroundLoading])

  const handlePlaygroundSend = async () => {
    if (!selectedAgent || !playgroundInput.trim() || isPlaygroundLoading) return

    const newMessages: PlaygroundMessage[] = [
      ...playgroundMessages,
      { role: 'user', content: playgroundInput.trim() },
    ]
    setPlaygroundMessages(newMessages)
    setPlaygroundInput('')
    setIsPlaygroundLoading(true)

    try {
      const res = await api.post(`/admin/ai-agents/${selectedAgent.id}/playground`, {
        messages: newMessages.map((m) => ({ role: m.role, content: m.content })),
      })
      setPlaygroundMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: res.data.reply || '',
          toolCalls: res.data.toolCalls || [],
          usage: res.data.usage,
        },
      ])
    } catch (error: any) {
      toast({
        title: 'Erro no playground',
        description: error.response?.data?.error || 'Tente novamente',
        status: 'error',
        duration: 6000,
      })
    } finally {
      setIsPlaygroundLoading(false)
    }
  }

  // ============ Render ============

  const renderAgentActions = (agent: AiAgent, size: 'xs' | 'sm') => (
    <HStack spacing={1} flexShrink={0}>
      <Tooltip label="Clientes vinculados">
        <IconButton
          aria-label="Clientes"
          icon={<UsersThree size={size === 'xs' ? 16 : 18} />}
          size={size}
          variant="ghost"
          onClick={() => handleOpenUsers(agent)}
        />
      </Tooltip>
      <Tooltip label="Playground de teste">
        <IconButton
          aria-label="Playground"
          icon={<Play size={size === 'xs' ? 16 : 18} />}
          size={size}
          variant="ghost"
          colorScheme="green"
          onClick={() => handleOpenPlayground(agent)}
        />
      </Tooltip>
      <IconButton
        aria-label="Editar"
        icon={<PencilSimple size={size === 'xs' ? 16 : 18} />}
        size={size}
        variant="ghost"
        onClick={() => handleEdit(agent)}
      />
      <IconButton
        aria-label="Excluir"
        icon={<Trash size={size === 'xs' ? 16 : 18} />}
        size={size}
        variant="ghost"
        colorScheme="red"
        onClick={() => handleDelete(agent)}
      />
    </HStack>
  )

  return (
    <>
      {isMobile && (
        <Drawer isOpen={isMenuOpen} placement="left" onClose={onMenuClose}>
          <DrawerOverlay />
          <DrawerContent bg={surfaceBg} maxW="280px">
            <DrawerCloseButton />
            <DrawerBody p={0}>
              <Sidebar onClose={onMenuClose} />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}

      <Box p={{ base: 3, md: 6 }} h="100%" overflowY="auto">
        <VStack align="stretch" spacing={{ base: 4, md: 6 }}>
          <HStack justify="space-between" spacing={3} flexWrap={{ base: 'wrap', md: 'nowrap' }}>
            <HStack spacing={3} align="center" flex={1} minW={0}>
              {isMobile && (
                <IconButton
                  aria-label="Menu"
                  icon={<List size={20} />}
                  variant="ghost"
                  onClick={onMenuOpen}
                  size="sm"
                  color={menuIconColor}
                  _hover={{ bg: borderColor }}
                  flexShrink={0}
                />
              )}
              <Text fontSize={{ base: 'lg', md: '2xl' }} fontWeight="bold" color={textPrimary}>
                Agentes de IA
              </Text>
              {!aiEnabled && (
                <Badge colorScheme="red" fontSize="xs">
                  IA desligada (killswitch)
                </Badge>
              )}
            </HStack>
            <Button
              leftIcon={<Plus size={18} />}
              colorScheme="yellow"
              onClick={handleCreate}
              size={{ base: 'sm', md: 'md' }}
              flexShrink={0}
            >
              <Text display={{ base: 'none', sm: 'block' }}>Novo Agente</Text>
              <Text display={{ base: 'block', sm: 'none' }}>Novo</Text>
            </Button>
          </HStack>

          <Tabs colorScheme="yellow" variant="enclosed">
            <TabList>
              <Tab>Agentes</Tab>
              <Tab>
                Atendimentos
                {!!chatSummary?.aiActive && (
                  <Badge ml={2} colorScheme="green" borderRadius="full">
                    {chatSummary?.aiActive}
                  </Badge>
                )}
              </Tab>
              <Tab>Prompt Base</Tab>
            </TabList>
            <TabPanels>
              {/* ============ Aba Agentes ============ */}
              <TabPanel px={0}>
                {isLoading ? (
                  <Spinner size="xl" color="yellow.500" />
                ) : agents.length === 0 ? (
                  <Text color={textMuted}>
                    Nenhum agente cadastrado. Crie um agente para uma categoria de atendimento.
                  </Text>
                ) : isMobile ? (
                  <VStack spacing={3} align="stretch">
                    {agents.map((agent) => (
                      <Box
                        key={agent.id}
                        bg={surfaceBg}
                        p={4}
                        borderRadius="lg"
                        border="1px"
                        borderColor={borderColor}
                      >
                        <VStack align="stretch" spacing={3}>
                          <HStack justify="space-between" align="start">
                            <VStack align="start" spacing={0} flex={1} minW={0}>
                              <Text color={textPrimary} fontWeight="semibold" fontSize="sm">
                                {agent.name}
                              </Text>
                              <Badge
                                fontSize="10px"
                                style={
                                  agent.categoryColor
                                    ? { backgroundColor: agent.categoryColor, color: 'white' }
                                    : undefined
                                }
                              >
                                {agent.categoryName || 'Sem categoria'}
                              </Badge>
                            </VStack>
                            <Switch
                              colorScheme="green"
                              isChecked={!!agent.isActive}
                              onChange={() => handleToggle(agent)}
                            />
                          </HStack>
                          <HStack justify="space-between" align="center">
                            <Text fontSize="xs" color={textMuted}>
                              {PROVIDER_LABELS[agent.provider]} · {agent.model}
                            </Text>
                            <Badge colorScheme={agent.activationMode === 'all' ? 'purple' : 'blue'} fontSize="xs">
                              {agent.activationMode === 'all' ? 'Todos' : `${agent.userCount} cliente(s)`}
                            </Badge>
                          </HStack>
                          {renderAgentActions(agent, 'xs')}
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                ) : (
                  <Box bg={surfaceBg} borderRadius="xl" overflow="hidden">
                    <Table>
                      <Thead>
                        <Tr>
                          <Th color={textMuted}>Agente</Th>
                          <Th color={textMuted}>Categoria</Th>
                          <Th color={textMuted}>Provedor / Modelo</Th>
                          <Th color={textMuted}>Ativação</Th>
                          <Th color={textMuted}>Ativo</Th>
                          <Th color={textMuted} w="180px">
                            Ações
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {agents.map((agent) => (
                          <Tr key={agent.id}>
                            <Td>
                              <HStack spacing={2}>
                                <Avatar size="xs" name={agent.name} />
                                <Text color={textPrimary} fontWeight="medium">
                                  {agent.name}
                                </Text>
                              </HStack>
                            </Td>
                            <Td>
                              <Badge
                                style={
                                  agent.categoryColor
                                    ? { backgroundColor: agent.categoryColor, color: 'white' }
                                    : undefined
                                }
                              >
                                {agent.categoryName || '—'}
                              </Badge>
                            </Td>
                            <Td>
                              <Text fontSize="sm" color={textPrimary}>
                                {PROVIDER_LABELS[agent.provider]}
                              </Text>
                              <Text fontSize="xs" color={textMuted}>
                                {agent.model}
                                {!agent.hasToken && ' · sem token!'}
                              </Text>
                            </Td>
                            <Td>
                              <Badge colorScheme={agent.activationMode === 'all' ? 'purple' : 'blue'}>
                                {agent.activationMode === 'all'
                                  ? 'Todos os clientes'
                                  : `${agent.userCount} cliente(s)`}
                              </Badge>
                            </Td>
                            <Td>
                              <Switch
                                colorScheme="green"
                                isChecked={!!agent.isActive}
                                onChange={() => handleToggle(agent)}
                              />
                            </Td>
                            <Td>{renderAgentActions(agent, 'sm')}</Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                )}
              </TabPanel>

              {/* ============ Aba Atendimentos (caixa de entrada estilo WhatsApp) ============ */}
              <TabPanel px={0} pb={0}>
                <AiChatsInbox agents={agents} onSummaryChange={setChatSummary} />
              </TabPanel>

              {/* ============ Aba Prompt Base ============ */}
              <TabPanel px={0}>
                <VStack align="stretch" spacing={4}>
                  <Text fontSize="sm" color={textMuted}>
                    Descritivo padrão (Markdown) comum a TODOS os agentes: persona, tom de voz, regras
                    gerais e instruções de uso das ferramentas. Cada agente complementa apenas com o
                    bloco de respostas da própria categoria.
                  </Text>
                  <FormControl display="flex" alignItems="center">
                    <Switch
                      colorScheme="green"
                      isChecked={aiEnabled}
                      onChange={(e) => setAiEnabled(e.target.checked)}
                      mr={3}
                    />
                    <FormLabel mb={0} fontSize="sm">
                      IA ligada (killswitch global — desligar interrompe TODOS os agentes imediatamente)
                    </FormLabel>
                  </FormControl>
                  <Textarea
                    value={basePrompt}
                    onChange={(e) => setBasePrompt(e.target.value)}
                    fontFamily="mono"
                    fontSize="sm"
                    minH="420px"
                    placeholder="# Atendente de Suporte WeDrop..."
                  />
                  <Button
                    colorScheme="yellow"
                    alignSelf="flex-end"
                    onClick={handleSaveBasePrompt}
                    isLoading={isSavingBasePrompt}
                  >
                    Salvar
                  </Button>
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </VStack>
      </Box>

      {/* ============ Modal Form ============ */}
      <Modal isOpen={isFormOpen} onClose={onFormClose} size={{ base: 'full', md: 'xl' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>{selectedAgent ? 'Editar' : 'Novo'} Agente de IA</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <FormControl isRequired>
                <FormLabel>Nome</FormLabel>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ex: Luana"
                />
                <FormHelperText>
                  Nome humano exibido ao cliente como atendente — um perfil de atendente é criado
                  automaticamente.
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Categoria</FormLabel>
                <Select
                  placeholder="Selecione a categoria"
                  value={formData.categoryId}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                >
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </Select>
                <FormHelperText>Cada categoria pode ter apenas um agente de IA.</FormHelperText>
              </FormControl>

              <HStack align="start" spacing={4}>
                <FormControl isRequired>
                  <FormLabel>Provedor</FormLabel>
                  <Select
                    value={formData.provider}
                    onChange={(e) => {
                      const provider = e.target.value as 'anthropic' | 'openai'
                      setFormData({
                        ...formData,
                        provider,
                        model: models[provider]?.[0] || '',
                      })
                    }}
                  >
                    <option value="anthropic">Claude (Anthropic)</option>
                    <option value="openai">OpenAI</option>
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Modelo</FormLabel>
                  <Select
                    value={formData.model}
                    onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                  >
                    {(models[formData.provider] || []).map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              </HStack>

              <FormControl isRequired={!selectedAgent?.hasToken}>
                <FormLabel>Token de API</FormLabel>
                <Input
                  type="password"
                  value={formData.apiToken}
                  onChange={(e) => setFormData({ ...formData, apiToken: e.target.value })}
                  placeholder={
                    selectedAgent?.hasToken
                      ? '•••• token salvo — preencha apenas para substituir'
                      : formData.provider === 'anthropic'
                        ? 'sk-ant-...'
                        : 'sk-...'
                  }
                />
              </FormControl>

              <FormControl>
                <FormLabel>Bloco de respostas da categoria (Markdown)</FormLabel>
                <Textarea
                  value={formData.responsePrompt}
                  onChange={(e) => setFormData({ ...formData, responsePrompt: e.target.value })}
                  fontFamily="mono"
                  fontSize="sm"
                  minH="260px"
                  placeholder={'## Perguntas frequentes\n\n**"Dúvida X"**\nResposta...\n\n## Fluxos de resolução\n...'}
                />
                <FormHelperText>
                  Liste as principais dúvidas desta categoria com as respostas prontas e os
                  fluxos/checklists passo a passo que a IA deve conduzir. O que não estiver coberto
                  aqui será transferido para um atendente humano.
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Ativação para clientes</FormLabel>
                <RadioGroup
                  value={formData.activationMode}
                  onChange={(value) =>
                    setFormData({ ...formData, activationMode: value as 'selected' | 'all' })
                  }
                >
                  <Stack direction="column" spacing={2}>
                    <Radio value="selected">Clientes selecionados (vincule na ação "Clientes")</Radio>
                    <Radio value="all">Todos os clientes</Radio>
                  </Stack>
                </RadioGroup>
                {formData.activationMode === 'all' && (
                  <Alert status="warning" mt={2} borderRadius="md" fontSize="sm">
                    <AlertIcon />
                    Com o agente ativo, TODOS os chamados novos e pendentes da categoria serão
                    atendidos pela IA.
                  </Alert>
                )}
              </FormControl>

              <FormControl display="flex" alignItems="center">
                <Switch
                  colorScheme="green"
                  isChecked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  mr={3}
                />
                <FormLabel mb={0}>Agente ativo</FormLabel>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onFormClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSubmit} isLoading={isSaving}>
              {selectedAgent ? 'Salvar' : 'Criar'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ Modal Usuários ============ */}
      <Modal isOpen={isUsersOpen} onClose={onUsersClose} size={{ base: 'full', md: 'lg' }} scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent bg={surfaceBg}>
          <ModalHeader>Clientes vinculados — {selectedAgent?.name}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              {selectedAgent?.activationMode === 'all' && (
                <Alert status="info" borderRadius="md" fontSize="sm">
                  <AlertIcon />
                  Este agente está no modo "Todos os clientes" — a lista abaixo só é usada se o modo
                  voltar para "Clientes selecionados".
                </Alert>
              )}
              <FormControl>
                <FormLabel>Buscar cliente (nome ou e-mail)</FormLabel>
                <Input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Digite ao menos 2 caracteres..."
                />
              </FormControl>

              {isSearchingUsers && <Spinner size="sm" color="yellow.500" />}

              {userResults.length > 0 && (
                <VStack
                  align="stretch"
                  spacing={1}
                  maxH="180px"
                  overflowY="auto"
                  border="1px"
                  borderColor={borderColor}
                  borderRadius="md"
                  p={2}
                >
                  {userResults.map((user) => {
                    const alreadyLinked = linkedUsers.some((u) => u.id === user.id)
                    return (
                      <HStack key={user.id} justify="space-between" p={1}>
                        <HStack spacing={2} minW={0}>
                          <Avatar size="xs" name={user.name} />
                          <VStack align="start" spacing={0} minW={0}>
                            <Text fontSize="sm" color={textPrimary} noOfLines={1}>
                              {user.name}
                            </Text>
                            <Text fontSize="xs" color={textMuted} noOfLines={1}>
                              {user.email}
                            </Text>
                          </VStack>
                        </HStack>
                        <Button
                          size="xs"
                          colorScheme="yellow"
                          variant={alreadyLinked ? 'ghost' : 'solid'}
                          isDisabled={alreadyLinked}
                          onClick={() => handleAddUser(user)}
                        >
                          {alreadyLinked ? 'Vinculado' : 'Adicionar'}
                        </Button>
                      </HStack>
                    )
                  })}
                </VStack>
              )}

              <Box>
                <Text fontSize="sm" fontWeight="semibold" color={textPrimary} mb={2}>
                  Vinculados ({linkedUsers.length})
                </Text>
                {linkedUsers.length === 0 ? (
                  <Text fontSize="sm" color={textMuted}>
                    Nenhum cliente vinculado ainda.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={1} maxH="220px" overflowY="auto">
                    {linkedUsers.map((user) => (
                      <HStack key={user.id} justify="space-between" p={1}>
                        <HStack spacing={2} minW={0}>
                          <Avatar size="xs" name={user.name} />
                          <VStack align="start" spacing={0} minW={0}>
                            <Text fontSize="sm" color={textPrimary} noOfLines={1}>
                              {user.name}
                            </Text>
                            <Text fontSize="xs" color={textMuted} noOfLines={1}>
                              {user.email}
                            </Text>
                          </VStack>
                        </HStack>
                        <IconButton
                          aria-label="Remover"
                          icon={<Trash size={14} />}
                          size="xs"
                          variant="ghost"
                          colorScheme="red"
                          onClick={() => handleRemoveUser(user.id)}
                        />
                      </HStack>
                    ))}
                  </VStack>
                )}
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onUsersClose}>
              Cancelar
            </Button>
            <Button colorScheme="yellow" onClick={handleSaveUsers} isLoading={isSavingUsers}>
              Salvar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ Modal Playground ============ */}
      <Modal
        isOpen={isPlaygroundOpen}
        onClose={onPlaygroundClose}
        size={{ base: 'full', md: 'xl' }}
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent bg={surfaceBg} maxH={{ md: '85vh' }}>
          <ModalHeader>
            <HStack spacing={2}>
              <Avatar size="sm" name={selectedAgent?.name} />
              <VStack align="start" spacing={0}>
                <Text>Playground — {selectedAgent?.name}</Text>
                <Text fontSize="xs" fontWeight="normal" color={textMuted}>
                  Teste seguro: nada é gravado e as ferramentas são apenas simuladas
                </Text>
              </VStack>
            </HStack>
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="stretch" spacing={3} minH="320px">
              {playgroundMessages.length === 0 && (
                <Text fontSize="sm" color={textMuted} textAlign="center" py={8}>
                  Envie uma mensagem como se você fosse o cliente.
                </Text>
              )}
              {playgroundMessages.map((msg, index) => (
                <Box key={index}>
                  <Box
                    bg={msg.role === 'user' ? bubbleUserBg : bubbleAgentBg}
                    px={3}
                    py={2}
                    borderRadius="lg"
                    maxW="85%"
                    ml={msg.role === 'user' ? 'auto' : 0}
                  >
                    <Text fontSize="sm" color={textPrimary} whiteSpace="pre-wrap">
                      {msg.content || (msg.toolCalls?.length ? '(sem texto — apenas ferramenta)' : '')}
                    </Text>
                  </Box>
                  {msg.toolCalls?.map((call, callIndex) => (
                    <Box
                      key={callIndex}
                      bg={toolCardBg}
                      px={3}
                      py={2}
                      mt={2}
                      borderRadius="md"
                      border="1px dashed"
                      borderColor="orange.400"
                      maxW="85%"
                    >
                      <HStack spacing={2}>
                        <Wrench size={16} />
                        <Text fontSize="sm" fontWeight="semibold" color={textPrimary}>
                          {call.name}
                        </Text>
                        <Badge colorScheme="orange" fontSize="10px">
                          simulado
                        </Badge>
                      </HStack>
                      {Object.keys(call.input || {}).length > 0 && (
                        <Text fontSize="xs" color={textMuted} mt={1} fontFamily="mono">
                          {JSON.stringify(call.input)}
                        </Text>
                      )}
                    </Box>
                  ))}
                  {msg.usage && (
                    <Text fontSize="10px" color={textMuted} mt={1}>
                      tokens: {msg.usage.inputTokens} entrada · {msg.usage.outputTokens} saída
                    </Text>
                  )}
                </Box>
              ))}
              {isPlaygroundLoading && (
                <HStack spacing={2}>
                  <Spinner size="sm" color="yellow.500" />
                  <Text fontSize="sm" color={textMuted}>
                    {selectedAgent?.name} está digitando...
                  </Text>
                </HStack>
              )}
              <div ref={playgroundEndRef} />
            </VStack>
          </ModalBody>
          <ModalFooter>
            <HStack w="100%" spacing={2}>
              <Tooltip label="Limpar conversa">
                <IconButton
                  aria-label="Limpar"
                  icon={<Eraser size={18} />}
                  variant="ghost"
                  onClick={() => setPlaygroundMessages([])}
                />
              </Tooltip>
              <Textarea
                value={playgroundInput}
                onChange={(e) => setPlaygroundInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault()
                    handlePlaygroundSend()
                  }
                }}
                placeholder="Mensagem do cliente de teste..."
                rows={1}
                resize="none"
              />
              <IconButton
                aria-label="Enviar"
                icon={<PaperPlaneTilt size={18} />}
                colorScheme="yellow"
                onClick={handlePlaygroundSend}
                isLoading={isPlaygroundLoading}
                isDisabled={!playgroundInput.trim()}
              />
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ============ Confirmação de exclusão ============ */}
      <AlertDialog isOpen={isDeleteOpen} leastDestructiveRef={cancelRef} onClose={onDeleteClose}>
        <AlertDialogOverlay>
          <AlertDialogContent bg={surfaceBg}>
            <AlertDialogHeader>Excluir Agente de IA</AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja excluir o agente "{selectedAgent?.name}"? Os chamados em
              andamento com este agente NÃO são transferidos automaticamente — desative o agente e
              aguarde os atendimentos terminarem antes de excluir.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onDeleteClose}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={handleConfirmDelete} ml={3}>
                Excluir
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  )
}
